import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Scissors, Award, Sparkles, Crown } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import './ModuleSelection.css';

const ModuleSelection = ({ onSelectModule }) => {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchModules = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/barber/service-modules`);

                if (!response.ok) {
                    throw new Error('Error al cargar los módulos');
                }

                const data = await response.json();
                setModules(data);
            } catch (err) {
                console.error('Error fetching modules:', err);
                setError(err.message);
                // Datos de ejemplo en caso de error
                setModules([
                    {
                        id: '1',
                        name: 'standard',
                        display_name: 'Barbero Standard',
                        description: 'Servicios básicos con precios accesibles',
                        image_url: null,
                        display_order: 1
                    },
                    {
                        id: '2',
                        name: 'proyeccion',
                        display_name: 'Barbero en Proyección',
                        description: 'Servicios intermedios con técnicas avanzadas',
                        image_url: null,
                        display_order: 2
                    },
                    {
                        id: '3',
                        name: 'pro',
                        display_name: 'Barbero Pro',
                        description: 'Servicios premium con máxima calidad',
                        image_url: null,
                        display_order: 3
                    },
                    {
                        id: '4',
                        name: 'andersson',
                        display_name: 'Barbero Profesional ANDERSSON',
                        description: 'Servicios premium de máxima calidad con técnicas avanzadas',
                        image_url: null,
                        display_order: 4
                    }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, []);

    const getModuleConfig = (moduleName, displayName) => {
        const name = (moduleName || '').toLowerCase();
        const display = (displayName || '').toLowerCase();

        if (name.includes('standard') || display.includes('standard')) {
            return { color: '#3b82f6', icon: <Scissors size={32} /> };
        }
        if (name.includes('proyeccion') || name.includes('proyección') || display.includes('proyeccion') || display.includes('proyección')) {
            return { color: '#10b981', icon: <Sparkles size={32} /> };
        }
        if (name === 'pro' || display === 'barbero pro' || (display.includes('pro') && !display.includes('proyección') && !display.includes('profesional'))) {
            return { color: '#f59e0b', icon: <Award size={32} /> };
        }
        if (name.includes('andersson') || display.includes('andersson')) {
            return { color: '#8b5cf6', icon: <Crown size={32} /> };
        }
        if (name.includes('elite') || display.includes('elite')) {
            return { color: '#8b5cf6', icon: <Crown size={32} /> };
        }

        return { color: '#6b7280', icon: <Scissors size={32} /> };
    };

    const getModuleColor = (module) => getModuleConfig(module?.name, module?.display_name).color;
    const getModuleIcon = (module) => getModuleConfig(module?.name, module?.display_name).icon;

    if (loading) {
        return (
            <div className="module-selection-loading">
                <div className="spinner"></div>
                <p>Cargando módulos de servicio...</p>
            </div>
        );
    }

    if (error && modules.length === 0) {
        return (
            <div className="module-selection-error">
                <p>Error al cargar los módulos: {error}</p>
            </div>
        );
    }

    return (
        <div className="module-selection">
            <div className="module-selection-header">
                <h2>Selecciona tu Plan de Servicio</h2>
                <p>Elige el módulo que mejor se adapte a tus necesidades</p>
            </div>

            <div className="modules-grid">
                {modules.map((module, index) => (
                    <motion.div
                        key={module.id}
                        className="module-card"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        onClick={() => onSelectModule(module)}
                        style={{
                            '--module-color': getModuleColor(module),
                            cursor: 'pointer'
                        }}
                    >
                        <div className="module-card-header">
                            <div
                                className="module-icon"
                                style={{ backgroundColor: `${getModuleColor(module)}20` }}
                            >
                                {getModuleIcon(module)}
                            </div>
                            <div className="module-badge" style={{ backgroundColor: getModuleColor(module) }}>
                                {module.display_name}
                            </div>
                        </div>

                        <div className="module-card-body">
                            <h3>{module.display_name}</h3>
                            <p className="module-description">
                                {module.description || 'Servicios de calidad profesional'}
                            </p>
                        </div>

                        <div className="module-card-footer">
                            <span className="select-text">Ver servicios y precios</span>
                            <ArrowRight size={20} />
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default ModuleSelection;

